import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

const CompanyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: '会社概要｜岡山市の印刷会社 おかやま印刷株式会社',
    description: 'おかやま印刷株式会社の会社概要、沿革、アクセス情報をご紹介します。1992年創業、岡山市北区に拠点を置き、地域に根ざした印刷サービスを提供しています。',
    keywords: '会社概要,会社情報,アクセス,岡山市北区,印刷会社,おかやま印刷',
    canonical: '/company',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '会社概要',
      description: 'おかやま印刷株式会社の会社概要、沿革、アクセス情報をご紹介します。',
      mainEntity: {
        '@type': 'Organization',
        name: 'おかやま印刷株式会社',
        foundingDate: '1992',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '北区○○町1-2-3',
          addressLocality: '岡山市',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP',
        },
        telephone: '+81-86-012-3456',
        faxNumber: '+81-86-012-3457',
      },
    },
  });

  const companyInfo = [
    { label: '会社名', value: 'おかやま印刷株式会社' },
    { label: '代表取締役', value: '岡山太郎' },
    { label: '設立', value: '1992年' },
    { label: '従業員数', value: '5名' },
    { label: '所在地', value: '岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX', value: '086-012-3457' },
    { label: '営業時間', value: '9:00 - 18:00' },
    { label: '定休日', value: '日曜日・祝日' },
    { label: '事業内容', value: '各種印刷業（紙印刷、シルク印刷、レーザー印刷）' },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-[#FF7A30] to-[#E85A20]"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              会社概要
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-white drop-shadow-md">
              岡山市で40年、地域に根ざした印刷会社
            </p>
          </div>
        </section>

        {/* Company Info Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                    会社情報
                  </h2>
                  <div className="space-y-4">
                    {companyInfo.map((info, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 pb-4 border-b border-gray-200"
                      >
                        <dt className="flex-shrink-0 w-full sm:w-32 text-sm md:text-base font-bold text-gray-600">
                          {info.label}
                        </dt>
                        <dd className="flex-1 text-base md:text-lg text-gray-900">
                          {info.value}
                        </dd>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <img
                    src="https://public.readdy.ai/ai/img_res/5aaa0f0c63e377ca04233d26ed892e60.jpg"
                    alt="おかやま印刷株式会社 外観"
                    className="w-full h-auto rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* History Section */}
        <section className="py-16 md:py-24 bg-primary-light">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                沿革
              </h2>
              <div className="space-y-6">
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-24 md:w-32">
                    <span className="inline-block px-4 py-2 bg-primary text-white font-bold rounded-lg">
                      1992年
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed">
                      岡山市北区にて創業。紙印刷を中心とした印刷業を開始。
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-24 md:w-32">
                    <span className="inline-block px-4 py-2 bg-primary text-white font-bold rounded-lg">
                      2000年
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed">
                      シルク印刷設備を導入。布製品への印刷サービスを開始。
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-24 md:w-32">
                    <span className="inline-block px-4 py-2 bg-primary text-white font-bold rounded-lg">
                      2010年
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed">
                      レーザー印刷機を導入。金属製品やステンレスボトルへの印刷を開始。
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-24 md:w-32">
                    <span className="inline-block px-4 py-2 bg-primary text-white font-bold rounded-lg">
                      2020年
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed">
                      最新鋭の印刷設備を導入。小ロットから大量生産まで幅広く対応可能に。
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-24 md:w-32">
                    <span className="inline-block px-4 py-2 bg-primary text-white font-bold rounded-lg">
                      現在
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed">
                      岡山市を中心に、地域のお客様に愛される印刷会社として事業を展開中。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                アクセス
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                <div>
                  <div className="bg-primary-light rounded-3xl p-6 md:p-8 mb-6">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                      所在地
                    </h3>
                    <p className="text-base md:text-lg text-gray-700 mb-6">
                      〒700-0000<br />
                      岡山市北区○○町1-2-3
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                      交通アクセス
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-train-line text-primary text-xl flex-shrink-0 mt-1"></i>
                        <span>JR岡山駅から車で約15分</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-bus-line text-primary text-xl flex-shrink-0 mt-1"></i>
                        <span>バス停「○○町」から徒歩3分</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-parking-box-line text-primary text-xl flex-shrink-0 mt-1"></i>
                        <span>駐車場完備（5台）</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-primary-light rounded-3xl p-6 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                      お問い合わせ
                    </h3>
                    <div className="space-y-3">
                      <a
                        href="tel:086-012-3456"
                        className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors cursor-pointer"
                      >
                        <i className="ri-phone-line text-2xl text-primary"></i>
                        <span className="text-lg font-bold">086-012-3456</span>
                      </a>
                      <div className="flex items-center gap-3 text-gray-700">
                        <i className="ri-printer-line text-2xl text-primary"></i>
                        <span>FAX: 086-012-3457</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <i className="ri-time-line text-2xl text-primary"></i>
                        <span>営業時間: 9:00 - 18:00</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-700">
                        <i className="ri-calendar-close-line text-2xl text-primary"></i>
                        <span>定休日: 日曜日・祝日</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="w-full h-full min-h-[400px] rounded-3xl overflow-hidden shadow-2xl">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.89476915!2d133.89!3d34.66!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e3b2f0e6e7%3A0x4a4b3e3e3e3e3e3e!2z5bKh5bGx5biC!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="おかやま印刷株式会社の地図"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/72f95f79575f04d2e5e777734d1956f7.jpeg"
              alt="印刷製品"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          <div className="relative z-10 px-4 md:px-8 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                お気軽にご来社ください
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-12">
                実際の印刷サンプルをご覧いただけます。<br className="md:hidden" />
                お電話でのご予約をおすすめします。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
                <a
                  href="tel:086-012-3456"
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white rounded-full font-bold text-base md:text-lg hover:bg-primary-dark transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  086-012-3456
                </a>
                <a
                  href="/contact"
                  className="w-full sm:w-auto px-8 py-4 bg-white text-primary border-2 border-primary rounded-full font-bold text-base md:text-lg hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-xl"></i>
                  お問い合わせフォーム
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CompanyPage;
